import { createTheme } from '@mui/material/styles';

const ardisGrey = '#4A4A4A';
const colors = {
    primary: ardisGrey,
    secondary: '#666666',
    background: '#ffffff',
    error: '#721c24',
    errorBackground: '#f8d7da',
    success: '#d4edda',
    text: {
        primary: '#333333',
        secondary: '#666666',
        disabled: '#6c757d'
    },
    divider: '#dee2e6',
    border: '#ced4da',
    hover: '#f8f9fa',
    focus: '#80bdff',
    focusShadow: 'rgba(0, 123, 255, 0.25)',
    chart: {
        colors: [
            "#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#8dd1e1", "#a4de6c", "#d0ed57", "#ffc0cb",
            "#d0ed57", "#a4de6c", "#8dd1e1", "#ff8042", "#ffc658", "#82ca9d", "#8884d8", "#ffc0cb",
            "#d0ed57", "#a4de6c", "#8dd1e1", "#ff8042", "#ffc658", "#82ca9d", "#8884d8", "#ffc0cb"
        ],
        primary: ardisGrey,
        secondary: '#868e96',
        success: '#28a745',
        info: '#17a2b8',
        warning: '#ffc107',
        danger: '#dc3545',
        light: '#f8f9fa',
        dark: '#343a40',
    }
};

// Common spacing
const spacing = {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem'
};

// Common mixins
const mixins = {
    cardContainer: {
        padding: spacing.md,
        marginBottom: spacing.md,
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    flexCenter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    flexBetween: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
};

const theme = createTheme({
    palette: {
        primary: {
            main: colors.primary,
        },
        secondary: {
            main: colors.secondary,
        },
        background: {
            default: colors.background,
            paper: colors.background,
        },
        text: {
            primary: colors.text.primary,
            secondary: colors.text.secondary,
        },
        error: {
            main: colors.error,
        },
        chart: {
            colors: colors.chart.colors,
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    margin: 0,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    backgroundColor: colors.background,
                },
                code: {
                    fontFamily: 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
                },
                '.text-align-right': {
                    textAlign: 'right',
                },
                '.flex-center': {
                    ...mixins.flexCenter,
                },
                '.flex-between': {
                    ...mixins.flexBetween,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.background,
                    boxShadow: 'none',
                    borderBottom: 'none',
                    padding: 0,
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: colors.primary,
                    height: '2px',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    color: colors.text.secondary,
                    '&.Mui-selected': {
                        color: colors.primary,
                    },
                    textTransform: 'uppercase',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    padding: '6px 16px',
                    letterSpacing: '0.5px',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    ...mixins.cardContainer,
                    '&.upload-container': {
                        padding: spacing.lg,
                        marginBottom: spacing.md,
                        maxWidth: '800px',
                        margin: '0 auto',
                    },
                    '&.data-grid': {
                        height: '100%',
                        width: '100%',
                        overflow: 'hidden',
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    marginBottom: spacing.sm,
                    '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                            borderColor: colors.hover,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: colors.focus,
                            boxShadow: `0 0 0 0.2rem ${colors.focusShadow}`,
                        },
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontWeight: 500,
                    '&.icon-button': {
                        minWidth: 0,
                        padding: '8px',
                    },
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    },
                },
                outlined: {
                    borderWidth: '2px',
                    '&:hover': {
                        borderWidth: '2px',
                    },
                },
            },
            variants: [
                {
                    props: { variant: 'navDropdown' },
                    style: {
                        color: colors.text.secondary,
                        backgroundColor: 'transparent',
                        textTransform: 'uppercase',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        letterSpacing: '0.5px',
                        marginLeft: spacing.md,
                        '&:hover': {
                            backgroundColor: 'transparent',
                            color: colors.primary,
                        },
                    },
                },
            ],
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    '&.upload-select': {
                        marginBottom: spacing.md,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 600,
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    padding: 0,
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    borderBottom: `1px solid ${colors.divider}`,
                    '&:last-child': {
                        borderBottom: 'none',
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: '4px',
                },
                standardSuccess: {
                    backgroundColor: colors.success,
                },
                standardError: {
                    backgroundColor: colors.errorBackground,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: '4px',
                },
            },
        },
    },
    typography: {
        h1: {
            fontSize: '2rem',
            fontWeight: 600,
            marginBottom: spacing.md,
            color: colors.text.primary,
        },
        h2: {
            fontSize: '1.75rem',
            fontWeight: 600,
            marginBottom: spacing.sm,
            color: colors.text.primary,
        },
        h3: {
            fontSize: '1.5rem',
            fontWeight: 600,
            marginBottom: spacing.sm,
            color: colors.text.primary,
        },
        h4: {
            fontSize: '1.25rem',
            fontWeight: 600,
            marginBottom: spacing.sm,
            color: colors.text.primary,
        },
        h5: {
            fontSize: '1.1rem',
            fontWeight: 600,
            marginBottom: spacing.sm,
            color: colors.text.primary,
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: spacing.sm,
            color: colors.text.primary,
        },
        subtitle1: {
            color: colors.text.secondary,
            fontSize: '1rem',
            marginBottom: spacing.xs,
        },
        subtitle2: {
            color: colors.text.secondary,
            fontSize: '0.875rem',
            marginBottom: spacing.xs,
        },
        body1: {
            marginBottom: spacing.sm,
            fontSize: '1rem',
        },
        body2: {
            marginBottom: spacing.sm,
            fontSize: '0.875rem',
        },
    },
    shape: {
        borderRadius: 8,
    },
    spacing: 8,
});

export default theme;
